import { useNode } from "@craftjs/core";
import React from "react";
import {
  ColorInput,
  SelectInput,
  UnitInput,
  ToggleInput,
  Panel,
  Tabs,
  TextInput,
  Spacer,
  AlignInput,
  RangeNumberInput,
  RichTextFormatting,
  TabsV2,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { changeTransition, isUpdatedFromDefault } from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";

export const CountUpSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    align,
    countStart = 0,
    countEnd = 100,
    countTime = 3,
    numPrefix = "",
    numSuffix = "",
    numFormat,
    numTSeparator,
    textColor,
    hoverTextColor,
    typoSize = "50px",
    typoLineHeight,
    typoLetterSpacing,
    typoWordSpacing,
    typoWeight,
    typoTransform,
    typoDecoration,
    typoStyle,
    transition,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing={"1rem"}>
          <TextInput
            minLength={INPUT_LIMITS["start-counter"].minlength}
            maxLength={INPUT_LIMITS["start-counter"].maxlength}
            resetValue={0}
            value={countStart}
            onChange={(val) => {
              setProp((props) => {
                props.countStart = val;
              });
            }}
            label={"Start"}
          />
          <TextInput
            minLength={INPUT_LIMITS["end-counter"].minlength}
            maxLength={INPUT_LIMITS["end-counter"].maxlength}
            resetValue={100}
            value={countEnd}
            onChange={(val) => {
              setProp((props) => {
                props.countEnd = val;
              });
            }}
            label={"End"}
          />
          <RangeNumberInput
            resetValue={3}
            value={countTime}
            onChange={(val) => {
              setProp((props) => {
                props.countTime = val;
              });
            }}
            min={0}
            step={0.1}
            max={10}
            label={"Time"}
            unit="sec"
          />
          <ToggleInput
            resetValue={undefined}
            label="Auto-format Number"
            value={numFormat}
            onChange={(val) => {
              setProp((props) => {
                props.numFormat = val;
              });
            }}
          />
          <div className="text-gray-400 text-xs mb-2">
            Numbers will be autoformatted to compact notation. Eg. 1100 will
            become 1.1K
          </div>
          <TextInput
            minLength={INPUT_LIMITS["counter-prefix"].minlength}
            maxLength={INPUT_LIMITS["counter-prefix"].maxlength}
            resetValue={""}
            value={numPrefix}
            onChange={(val) => {
              setProp((props) => {
                props.numPrefix = val;
              });
            }}
            label={"Number Prefix"}
          />
          <TextInput
            minLength={INPUT_LIMITS["counter-suffix"].minlength}
            maxLength={INPUT_LIMITS["counter-suffix"].maxlength}
            resetValue={""}
            value={numSuffix}
            onChange={(val) => {
              setProp((props) => {
                props.numSuffix = val;
              });
            }}
            label={"Number Suffix"}
          />
          {numFormat === undefined || numFormat === false ? (
            <SelectInput
              resetValue={undefined}
              value={numTSeparator}
              onChange={(val) => {
                setProp((props) => {
                  props.numTSeparator = val;
                });
              }}
              options={[
                {
                  label: ",",
                  value: ",",
                },
                {
                  label: ".",
                  value: ".",
                },
              ]}
              label="Thousands Separator"
            />
          ) : null}
          <AlignInput
            resetValue={undefined}
            value={align}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            label={"Align"}
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: typoSize, default: "50px" },
            { key: typoLineHeight },
            { key: typoLetterSpacing },
            { key: typoWordSpacing },
            { key: typoWeight },
            { key: typoTransform },
            { key: typoDecoration },
            { key: typoStyle },
          ])}
          id={`block-setting-${id}-typography`}
          title={"Typography"}
          initialOpen={false}
        >
          <UnitInput
            resetValue={"50px"}
            value={typoSize}
            onChange={(val) => {
              setProp((props) => {
                props.typoSize = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            resetValue={undefined}
            value={typoWeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoWeight = val;
              });
            }}
            options={[
              { label: "100", value: "100" },
              { label: "200", value: "200" },
              { label: "300", value: "300" },
              { label: "400", value: "400" },
              { label: "500", value: "500" },
              { label: "600", value: "600" },
              { label: "700", value: "700" },
              { label: "800", value: "800" },
              { label: "900", value: "900" },
            ]}
            label="Weight"
          />
          <SelectInput
            resetValue={undefined}
            value={typoTransform}
            onChange={(val) => {
              setProp((props) => {
                props.typoTransform = val;
              });
            }}
            options={[
              { label: "Uppercase", value: "uppercase" },
              { label: "Lowercase", value: "lowercase" },
              { label: "Capitalize", value: "capitalize" },
              { label: "None", value: "none" },
            ]}
            label="Transform"
          />
          <SelectInput
            resetValue={undefined}
            value={typoDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.typoDecoration = val;
              });
            }}
            options={[
              { label: "Underline", value: "underline" },
              { label: "Overline", value: "overline" },
              { label: "None", value: "none" },
            ]}
            label="Decoration"
          />
          <SelectInput
            resetValue={undefined}
            value={typoStyle}
            onChange={(val) => {
              setProp((props) => {
                props.typoStyle = val;
              });
            }}
            options={[
              {
                label: "Normal",
                value: "normal",
              },
              {
                label: "Italic",
                value: "italic",
              },
            ]}
            label="Style"
          />
        </Panel>
        <RichTextFormatting />
      </div>
    );
  }

  function StyleSettings() {
    const colorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleColorStates({
          textColorPropKey: "textColor",
          textColorProp: textColor,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleColorStates({
          textColorPropKey: "hoverTextColor",
          textColorProp: hoverTextColor,
        }),
      },
    ];

    function handleColorStates({ textColorPropKey, textColorProp }) {
      return (
        <>
          <ColorInput
            resetValue={undefined}
            value={textColorProp}
            onChange={(val) => {
              setProp((props) => {
                props[textColorPropKey] = val;
              });
            }}
            label={"Text"}
          />
          {textColorPropKey === "hoverTextColor" && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={transition?.types?.["color"]?.duration}
              onChange={(val) => {
                changeTransition(
                  ["color"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: textColor },
              { key: hoverTextColor },
            ])}
            id={`block-setting-${id}-color`}
            title={"Color"}
            initialOpen={true}
          >
            <TabsV2 items={colorTabs} />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          {supports &&
            supports.map((support) => {
              var Component = supportLibrary[support]?.component;

              if (!Component) {
                return;
              }

              return (
                <>
                  <Component
                    block={{
                      id,
                      props,
                      dom,
                    }}
                    actions={{
                      setProp,
                    }}
                  ></Component>
                </>
              );
            })}
        </div>
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: countStart },
        { key: countEnd },
        { key: countTime },
        { key: numFormat },
        { key: numPrefix },
        { key: numSuffix },
        { key: numTSeparator },
        { key: align },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: textColor },
        { key: textColor },
        { key: typoSize, default: "50px" },
        { key: typoLineHeight },
        { key: typoLetterSpacing },
        { key: typoWordSpacing },
        { key: typoWeight },
        { key: typoTransform },
        { key: typoDecoration },
        { key: typoStyle },
        { key: hoverTextColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([]),
    },
  ];

  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
